import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError, ApiAuthError } from '../lib/api';
import { Badge, Select, IconAlertTriangle } from '../atoms';
import { StatCard } from '../organisms';
import { formatDate } from '../lib/orgTimezone';

/**
 * The standing disclaimer on every state-rules view (Pepco Jul-15 §B5).
 *
 * This page states appeal windows, assessment ratios and rate methodology — figures a
 * firm could act on and a deadline they could miss. Statutes change, a jurisdiction can
 * deviate from its state's general rule, and the seeded data has an effective date that
 * is not the same thing as "current". Saying so plainly, on the page, at the moment
 * someone is reading the number, is the only placement that actually protects anyone; a
 * line in a contract nobody re-reads does not.
 *
 * Deliberately worded as guidance rather than legalese, because a notice people skim
 * past protects no one either. NOTE: this copy has NOT been through counsel (PRD OQ-3) —
 * it is written to be conservative in the meantime, and counsel's version should replace
 * it verbatim rather than being merged with it.
 */
function StateRulesDisclaimer() {
  return (
    <div
      role="note"
      style={{
        display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-start',
        padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-5)',
        background: 'var(--color-status-amber-bg)',
        border: '1px solid var(--color-status-amber)',
        borderRadius: 'var(--r-lg)',
      }}
    >
      {/* Line icon, not an emoji: emoji render as a different glyph per platform and
          carry colour we do not control (part 4 §6). */}
      <IconAlertTriangle size={18} style={{ flexShrink: 0, color: 'var(--color-status-amber)' }} />
      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>
        <strong style={{ display: 'block', marginBottom: 2 }}>Reference only — verify before you rely on it.</strong>
        These rules are a working aid for the calculation engine, not legal advice and not a
        substitute for the statute. Deadlines, ratios and procedures change, and an individual
        jurisdiction may differ from its state's general rule. Confirm anything that affects a
        filing, a deadline or a client's money against the taxing authority or your counsel
        before acting on it.
      </div>
    </div>
  );
}

/**
 * StateRules — /state-rules
 *
 * Reference viewer for the per-state rules + terminology the calc engine
 * resolves against, scoped to the seeded states (TX + GA). Operators/platform
 * admins see, per state: the assessment ratio, rate methodology (tax rate vs
 * millage), assessment cycle, appeal window, and the local vernacular
 * (Appraisal Review Board for TX vs Board of Equalization for GA).
 *
 * Reads GET /api/state-rules (list), /api/state-rules/:code, and
 * /api/terminology/:code — all live in src/routes/calc.js (contracts frozen in
 * the Domain 11 PRD). If a route is ever unreachable (404/405) the viewer
 * degrades to a typed offline sample instead of a blank page. The state selector
 * is data-driven from the list — it grows to N states as they are seeded, never a
 * 51-item dropdown.
 *
 * Domain 11.1 — Multi-State Engine & Terminology. Gated to super_admin/admin.
 */

interface StateRule {
  state_code: string;
  state_name: string;
  re_assessment_ratio: number;
  pp_assessment_ratio: number;
  uses_millage_rate: boolean;
  rate_terminology: string;
  rate_divisor: number;
  assessment_cycle_years: number;
  taxes_are_net: boolean;
  fiscal_basis: string;
  appeal_window_days: number;
  version: number;
  effective_date: string;
}

interface Terminology {
  rate_terminology: string;
  appeal_labels: { informal_hearing: string; formal_hearing: string; arbitration: string; litigation: string };
  notice_term: string;
  form_names: { aoa: string; appeal: string };
  appeal_body: string;
}

// Offline samples modeling the frozen contracts (TX baseline + GA). Shown only
// as a fallback when the live /api/state-rules routes are unreachable (404/405).
const MOCK_RULES: Record<string, StateRule> = {
  TX: {
    state_code: 'TX', state_name: 'Texas', re_assessment_ratio: 1.0, pp_assessment_ratio: 1.0,
    uses_millage_rate: false, rate_terminology: 'Tax Rate', rate_divisor: 100, assessment_cycle_years: 1,
    taxes_are_net: false, fiscal_basis: 'calendar', appeal_window_days: 30, version: 1, effective_date: '2026-01-01',
  },
  GA: {
    state_code: 'GA', state_name: 'Georgia', re_assessment_ratio: 0.4, pp_assessment_ratio: 0.4,
    uses_millage_rate: true, rate_terminology: 'Millage Rate', rate_divisor: 1000, assessment_cycle_years: 3,
    taxes_are_net: false, fiscal_basis: 'calendar', appeal_window_days: 45, version: 1, effective_date: '2026-01-01',
  },
};
const MOCK_TERMS: Record<string, Terminology> = {
  TX: {
    rate_terminology: 'Tax Rate', appeal_body: 'Appraisal Review Board', notice_term: 'Notice of Appraised Value',
    appeal_labels: { informal_hearing: 'Informal Conference', formal_hearing: 'ARB Hearing', arbitration: 'Binding Arbitration', litigation: 'District Court Appeal' },
    form_names: { aoa: 'Agent Authorization (AoA)', appeal: 'Notice of Protest' },
  },
  GA: {
    rate_terminology: 'Millage Rate', appeal_body: 'Board of Equalization', notice_term: 'Notice of Assessment',
    appeal_labels: { informal_hearing: 'Informal Review', formal_hearing: 'BOE Hearing', arbitration: 'Arbitration', litigation: 'Superior Court Appeal' },
    form_names: { aoa: 'Agent Authorization', appeal: 'PT-311A Appeal' },
  },
};

async function probe<T>(path: string, mock: T, signal?: AbortSignal): Promise<{ data: T; preview: boolean }> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 3500);
  signal?.addEventListener('abort', () => ctrl.abort());
  try {
    const data = await api.get<T>(path, ctrl.signal);
    return { data, preview: false };
  } catch (err) {
    if (err instanceof ApiClientError && !(err.status === 404 || err.status === 405)) throw err;
    return { data: mock, preview: true };
  } finally {
    clearTimeout(timer);
  }
}

export function StateRules() {
  const [stateCode, setStateCode] = useState('TX');

  const userQuery = useQuery<{ role: string }>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<{ role: string }>('/api/current-user', signal),
    retry: (n, e) => (e instanceof ApiAuthError ? false : n < 2),
  });

  const listQuery = useQuery<{ data: StateRule[]; preview: boolean }>({
    queryKey: ['state-rules-list'],
    queryFn: ({ signal }) => probe<StateRule[]>('/api/state-rules', Object.values(MOCK_RULES), signal),
  });

  const ruleQuery = useQuery<{ data: StateRule; preview: boolean }>({
    queryKey: ['state-rule', stateCode],
    queryFn: ({ signal }) => probe<StateRule>(`/api/state-rules/${stateCode}`, MOCK_RULES[stateCode] ?? MOCK_RULES.TX, signal),
  });

  const termQuery = useQuery<{ data: Terminology; preview: boolean }>({
    queryKey: ['terminology', stateCode],
    queryFn: ({ signal }) => probe<Terminology>(`/api/terminology/${stateCode}`, MOCK_TERMS[stateCode] ?? MOCK_TERMS.TX, signal),
  });

  const options = useMemo(
    () => (listQuery.data?.data ?? []).map(s => ({ value: s.state_code, label: `${s.state_name} (${s.state_code})` })),
    [listQuery.data],
  );

  if (userQuery.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>;
  if (userQuery.data && userQuery.data.role !== 'super_admin' && userQuery.data.role !== 'admin' && userQuery.data.role !== 'administrator') {
    return (
      <div style={{
        padding: 'var(--sp-5)', background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)', borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>Access denied</h2>
        <p style={{ margin: 0 }}>State rules are restricted to super_admin and admin roles.</p>
      </div>
    );
  }

  const rule = ruleQuery.data?.data;
  const terms = termQuery.data?.data;
  const preview = (listQuery.data?.preview || ruleQuery.data?.preview || termQuery.data?.preview) ?? false;

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0, fontSize: 'var(--fs-3xl)', fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)', color: 'var(--color-ink)',
        }}>State rules & terminology</h1>
        <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
          The per-state rules and local vernacular the calc engine resolves against. Seeded states only.
          {preview && <span style={{ color: 'var(--color-muted)' }}> · offline sample (live rules unreachable)</span>}
        </p>
      </header>

      <StateRulesDisclaimer />

      <div style={{ maxWidth: 320, marginBottom: 'var(--sp-6)' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span style={{
            fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase',
            letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
          }}>State</span>
          <Select options={options} value={stateCode} onChange={e => setStateCode(e.target.value)} />
        </label>
      </div>

      {rule && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
          gap: 'var(--sp-4)', marginBottom: 'var(--sp-6)',
        }}>
          <StatCard label="RE assessment ratio" value={`${Math.round(rule.re_assessment_ratio * 100)}%`} caption={`PP ${Math.round(rule.pp_assessment_ratio * 100)}%`} accent="hunter" />
          <StatCard label="Rate methodology" value={rule.rate_terminology} caption={rule.uses_millage_rate ? `Millage · ÷${rule.rate_divisor}` : `Rate · ÷${rule.rate_divisor}`} accent="info" />
          <StatCard label="Assessment cycle" value={`${rule.assessment_cycle_years} yr`} caption={rule.fiscal_basis === 'calendar' ? 'Calendar basis' : 'Fiscal basis'} accent="amber" />
          <StatCard label="Appeal window" value={`${rule.appeal_window_days} days`} caption={`v${rule.version} · from ${formatDate(rule.effective_date)}`} accent="green" />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 'var(--sp-4)' }}>
        {rule && (
          <InfoCard label="Rules">
            <Row label="Millage rate" value={rule.uses_millage_rate ? <Badge variant="info">Millage</Badge> : <Badge variant="neutral">Flat rate</Badge>} />
            <Row label="Rate divisor" value={String(rule.rate_divisor)} />
            <Row label="Taxes are net" value={rule.taxes_are_net ? 'Yes' : 'No'} />
            <Row label="Fiscal basis" value={rule.fiscal_basis} />
          </InfoCard>
        )}
        {terms && (
          <InfoCard label="Terminology">
            <Row label="Appeal body" value={<Badge variant="info" dot>{terms.appeal_body}</Badge>} />
            <Row label="Notice term" value={terms.notice_term} />
            <Row label="Rate label" value={terms.rate_terminology} />
            <Row label="Appeal form" value={terms.form_names.appeal} />
            <Row label="Authorization" value={terms.form_names.aoa} />
          </InfoCard>
        )}
        {terms && (
          <InfoCard label="Appeal stages (local labels)">
            <Row label="Informal" value={terms.appeal_labels.informal_hearing} />
            <Row label="Formal" value={terms.appeal_labels.formal_hearing} />
            <Row label="Arbitration" value={terms.appeal_labels.arbitration} />
            <Row label="Litigation" value={terms.appeal_labels.litigation} />
          </InfoCard>
        )}
      </div>
    </>
  );
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)',
    }}>
      <h3 style={{
        margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
      }}>{label}</h3>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)',
      padding: 'var(--sp-1) 0', fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', textAlign: 'right' }}>{value}</span>
    </div>
  );
}
